import { FC, useState } from 'react';
import { Flex, Heading, Icon, useToast, VStack } from '@chakra-ui/react';
import {
  useAppContext,
  useGetRegistration,
  useSendTafMessage,
  useUpdateAcknowledgeGifts,
  useUpdateRegistration,
} from '@frx/frx-react-context';
import { SendTafMessageOptions } from '@frx/frx-services';
import {
  Card,
  Donation,
  DonationProps,
  DonationFilter,
  PaginationList,
} from '../../../components';
import { Bell } from '../../../assets';
import { DonationsNotificationsModal } from './DonationsNotificationsModal';
import { useEventId } from '../../../hooks';
import { getDataTracking } from '../../../utils';

export interface DonationsMainColumnProps {
  donations: DonationProps[];
  isLoading?: boolean;
}

export const DonationsMainColumn: FC<DonationsMainColumnProps> = ({
  donations,
  isLoading,
}): JSX.Element => {
  const eventId = useEventId();
  const {
    data: {
      eventData: { eventConfig },
    },
  } = useAppContext();

  const [showUnthankedOnly, setShowUnthankedOnly] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState('');
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);

  const toast = useToast();

  const selectFilteredDonations = (): DonationProps[] =>
    donations
      .filter((donation) =>
        donation?.name?.toLowerCase().includes(searchCriteria.toLowerCase())
      )
      .filter(
        (donation) =>
          (showUnthankedOnly && !donation.thanked) || !showUnthankedOnly
      );

  const onSearchChange = (search: string) => {
    setSearchCriteria(search);
  };

  // Donation Notification Data
  const { data: registrationData } = useGetRegistration();
  const { mutate: updateRegistration } = useUpdateRegistration();
  const { mutate: sendThankYouMessage } = useSendTafMessage();
  const { mutate: updateAcknowledgeGifts } = useUpdateAcknowledgeGifts();

  const handleUpdateDonationNotification = (checked: boolean) => {
    updateRegistration({
      fr_id: eventId,
      receive_gift_notification: checked,
    });
  };

  const receiveNotifications = registrationData?.registration
    ?.receiveGiftNotification as boolean;

  const onThanked = (
    contactId: number,
    messageOpts: Pick<
      SendTafMessageOptions,
      'recipients' | 'subject' | 'message_body'
    >
  ) => {
    sendThankYouMessage(
      {
        fr_id: Number(eventId),
        layout_id: eventConfig?.defaultStationeryId || 0,
        subject: messageOpts?.subject || '',
        recipients: messageOpts?.recipients || '',
        message_body: messageOpts?.message_body || '',
      },
      {
        onSuccess: () => {
          updateAcknowledgeGifts(contactId);
          toast({
            title: 'Your message was successfully sent.',
            status: 'success',
            duration: 3000,
          });
        },
      }
    );
  };

  // Show days left option if eventDate is today or in the future
  const onToggleThanked = () => {
    setShowUnthankedOnly(() => !showUnthankedOnly);
  };

  return (
    <Card>
      <Flex justifyContent="space-between">
        <Heading size="md" color="brand.primary.swatch1">
          Latest Donations
        </Heading>
        <Icon
          color="brand.primary.swatch1"
          aria-label="notifications-modal"
          as={Bell}
          boxSize="30px"
          cursor="pointer"
          {...getDataTracking('notificationIconToggle')}
          onClick={() => {
            setShowNotificationsModal(true);
          }}
        />
      </Flex>
      <DonationsNotificationsModal
        isOpen={showNotificationsModal}
        receiveNotifications={receiveNotifications}
        onToggle={(checked) => handleUpdateDonationNotification(checked)}
        onClosed={() => {
          setShowNotificationsModal(false);
        }}
      />

      <VStack mt={4} spacing={8} align="stretch">
        <DonationFilter
          onChanged={onSearchChange}
          onToggleThanked={onToggleThanked}
        />
        <PaginationList
          isLoading={isLoading}
          trackingLabel="latestDonations"
          listKey="contactId"
          listItems={selectFilteredDonations().map((donation) => ({
            ...donation,
            onThanked,
          }))}
        >
          <Donation />
        </PaginationList>
      </VStack>
    </Card>
  );
};
